import { windowWidth, windowHeight, GAME_VERSION } from './constants';
import { makeButton, saveSetting, onAnyInputOnce } from './util';
import { SpaceScene } from './SpaceScene';
import type { UIScene } from './UIScene';


// Title / splash screen: the game world (sky + drifting asteroids) behind a
// panel with the title and instructions. Any key starts a fresh game. The
// game-over screen returns here, so the game is replayable without a reload.
export class Splash extends SpaceScene {
    constructor() {
        super('splash');
    }

    create() {
        const w = windowWidth;
        const h = windowHeight;

        // No in-game HUD on the splash screen.
        (this.scene.get('UIScene') as UIScene).setHudVisible(false);

        // Music plays only in-game, so make sure it's stopped on the menu
        // (covers returning here after a game over).
        const music = this.registry.get('music') as any;
        if (music) music.stop();

        // Sky + drifting asteroids backdrop.
        this.createBackdrop();

        // Foreground panel with the title and instructions.
        const cx = w / 2;
        const cy = h / 2;
        const panelW = 660;
        const panelH = 320;
        const panel = this.add.graphics();
        panel.fillStyle(0x000018, 0.72);
        panel.fillRoundedRect(cx - panelW / 2, cy - panelH / 2, panelW, panelH, 16);
        panel.lineStyle(2, 0x6cb8ff, 0.9);
        panel.strokeRoundedRect(cx - panelW / 2, cy - panelH / 2, panelW, panelH, 16);

        this.add.text(cx, cy - 110, 'SPACE GAME', {
            fontFamily: 'Arial Black, Arial', fontSize: '64px', color: '#ffffff'
        }).setOrigin(0.5);

        this.add.text(cx, cy - 25,
            'Find the space station to reach the next level.\nAvoid the asteroids. Beware of the enemies.', {
            fontFamily: 'Arial', fontSize: '20px', color: '#cfe8ff', align: 'center'
        }).setOrigin(0.5);

        this.add.text(cx, cy + 45,
            'Thrust = Cursor UP      Rotate = LEFT / RIGHT      Shoot = SPACE', {
            fontFamily: 'Arial', fontSize: '18px', color: '#9fb8d0', align: 'center'
        }).setOrigin(0.5);

        this.add.text(cx, cy + 72, 'Esc / P = Pause      M = Mute music      … or use a controller', {
            fontFamily: 'Arial', fontSize: '15px', color: '#7f93a8'
        }).setOrigin(0.5);

        const prompt = this.add.text(cx, cy + 110, 'Press any key to start', {
            fontFamily: 'Arial', fontSize: '24px', color: '#ffd34d'
        }).setOrigin(0.5);
        this.tweens.add({ targets: prompt, alpha: 0.2, duration: 700, yoyo: true, repeat: -1 });

        // Credits button (below the panel) - opens the credits screen on click.
        makeButton(this, cx, cy + 200, 'Credits', () => this.scene.start('credits'));

        // Build version, low-key in the bottom-right corner. The pipeline stamps
        // GAME_VERSION at deploy; locally it shows the unstamped dev placeholder.
        this.add.text(w - 12, h - 10, 'v' + GAME_VERSION, {
            fontFamily: 'Arial', fontSize: '14px', color: '#5b6b7d'
        }).setOrigin(1, 1).setAlpha(0.8);

        // Start a fresh game on any key or controller button. Short guard so an
        // input still held from the game-over screen doesn't instantly skip past
        // the splash. (Clicking the Credits button is a pointer event, so it
        // won't trigger this.)
        this.time.delayedCall(350, () => {
            onAnyInputOnce(this, () => this.scene.start('level', { level: 1 }));
        });
    }
}

// Credits screen: the same drifting-asteroids backdrop with a panel listing the
// people who made the game. A Back button (or any key) returns to the splash.
export class Credits extends SpaceScene {
    constructor() {
        super('credits');
    }

    create() {
        const w = windowWidth;
        const h = windowHeight;
        const cx = w / 2;
        const cy = h / 2;

        (this.scene.get('UIScene') as UIScene).setHudVisible(false);
        this.createBackdrop();

        const panelW = 640;
        const panelH = 400;
        const panel = this.add.graphics();
        panel.fillStyle(0x000018, 0.78);
        panel.fillRoundedRect(cx - panelW / 2, cy - panelH / 2, panelW, panelH, 16);
        panel.lineStyle(2, 0x6cb8ff, 0.9);
        panel.strokeRoundedRect(cx - panelW / 2, cy - panelH / 2, panelW, panelH, 16);

        this.add.text(cx, cy - 155, 'CREDITS', {
            fontFamily: 'Arial Black, Arial', fontSize: '46px', color: '#ffffff'
        }).setOrigin(0.5);

        this.add.text(cx, cy - 95, 'A Hackathon game by the Bennedik family and friends', {
            fontFamily: 'Arial', fontSize: '17px', color: '#9fb8d0'
        }).setOrigin(0.5);

        const roles = [
            ['Development', 'Martin Bennedik'],
            ['Sound effects', 'Marco Schmoll'],
            ['Graphic design', 'Marcel Bennedik'],
            ['Game design', 'Maximilian Bennedik']
        ];
        const startY = cy - 40;
        const rowH = 34;
        for (let i = 0; i < roles.length; i++) {
            this.add.text(cx - 30, startY + i * rowH, roles[i][0], {
                fontFamily: 'Arial', fontSize: '20px', color: '#9fb8d0'
            }).setOrigin(1, 0.5);
            this.add.text(cx + 30, startY + i * rowH, roles[i][1], {
                fontFamily: 'Arial', fontSize: '20px', color: '#ffffff'
            }).setOrigin(0, 0.5);
        }

        this.add.text(cx, cy + 110, 'Engine: Phaser      Ship graphics: MillionthVector', {
            fontFamily: 'Arial', fontSize: '16px', color: '#9fb8d0'
        }).setOrigin(0.5);

        // Build version (the pipeline stamps GAME_VERSION at deploy).
        this.add.text(cx, cy + 142, 'Version ' + GAME_VERSION, {
            fontFamily: 'Arial', fontSize: '15px', color: '#7f93a8'
        }).setOrigin(0.5);

        makeButton(this, cx, cy + 178, 'Back', () => this.scene.start('splash'));

        // Any key or controller button also returns to the splash.
        this.time.delayedCall(250, () => {
            onAnyInputOnce(this, () => this.scene.start('splash'));
        });
    }
}

// Pause overlay (P during a game): dims the frozen game and shows volume
// controls for the music and the sound effects, plus a Resume button.
export class Pause extends Phaser.Scene {
    constructor() {
        super('pause');
    }

    create() {
        const w = windowWidth;
        const h = windowHeight;
        const cx = w / 2;
        const cy = h / 2;

        // Dim the frozen game behind the menu.
        this.add.rectangle(0, 0, w, h, 0x000000, 0.55).setOrigin(0, 0);

        const panelW = 560;
        const panelH = 360;
        const panel = this.add.graphics();
        panel.fillStyle(0x000018, 0.9);
        panel.fillRoundedRect(cx - panelW / 2, cy - panelH / 2, panelW, panelH, 16);
        panel.lineStyle(2, 0x6cb8ff, 0.9);
        panel.strokeRoundedRect(cx - panelW / 2, cy - panelH / 2, panelW, panelH, 16);

        this.add.text(cx, cy - 128, 'PAUSED', {
            fontFamily: 'Arial Black, Arial', fontSize: '48px', color: '#ffffff'
        }).setOrigin(0.5);

        this.addVolumeControl(cy - 48, 'MUSIC', 'musicVolume', (v) => this.applyMusicVolume(v));
        this.addVolumeControl(cy + 12, 'EFFECTS', 'sfxVolume', (v) => this.applySfxVolume(v));

        makeButton(this, cx, cy + 100, 'Resume', () => this.resumeGame());

        this.add.text(cx, cy + 148, 'Press P or Esc to resume', {
            fontFamily: 'Arial', fontSize: '15px', color: '#7f93a8'
        }).setOrigin(0.5);

        // Resume on P / Esc too, after a short guard so the key-press that paused
        // the game doesn't immediately carry over and resume it.
        this.time.delayedCall(200, () => {
            this.input.keyboard.once('keydown-P', () => this.resumeGame());
            this.input.keyboard.once('keydown-ESC', () => this.resumeGame());
        });
    }

    // Poll the gamepad Start button to resume (edge-triggered). startWasDown
    // starts true so the Start press that paused the game (if still held) does
    // not immediately resume it.
    private startWasDown: boolean = true;
    update() {
        if (this.input.gamepad && this.input.gamepad.total > 0) {
            const pad = this.input.gamepad.getPad(0);
            const startNow = !!(pad && pad.buttons[9] && pad.buttons[9].pressed);
            if (startNow && !this.startWasDown) {
                this.resumeGame();
            }
            this.startWasDown = startNow;
        }
    }

    // A label + [-] [value%] [+] row that adjusts a registry volume in 10% steps.
    private addVolumeControl(y: number, label: string, key: string, apply: (v: number) => void) {
        const cx = windowWidth / 2;

        this.add.text(cx - 200, y, label, {
            fontFamily: 'Arial', fontSize: '20px', color: '#cfe8ff'
        }).setOrigin(0, 0.5);

        const valueText = this.add.text(cx + 78, y, '', {
            fontFamily: 'Arial Black, Arial', fontSize: '20px', color: '#ffffff'
        }).setOrigin(0.5, 0.5);

        const refresh = () => valueText.setText(`${Math.round(this.registry.get(key) * 100)}%`);
        const change = (delta: number) => {
            const current = this.registry.get(key);
            const v = Phaser.Math.Clamp(Math.round((current + delta) * 10) / 10, 0, 1);
            this.registry.set(key, v);
            saveSetting(key, v); // remember it across reloads
            refresh();
            apply(v);
        };

        makeButton(this, cx + 20, y, '–', () => change(-0.1));
        makeButton(this, cx + 136, y, '+', () => change(0.1));
        refresh();
    }

    private applyMusicVolume(v: number) {
        const music = this.registry.get('music') as any;
        if (music) {
            music.setVolume(v);
        }
    }

    private applySfxVolume(v: number) {
        // Update the (paused) level's SFX objects so they use the new volume on
        // resume, and play a short blip so the change is audible right away.
        const level = this.scene.get('level') as any;
        if (level && level.setSfxVolume) {
            level.setSfxVolume(v);
        }
        this.sound.play('laser', { volume: v });
    }

    private resumeGame() {
        // Resume the music if auto-pause paused it (a no-op when it kept playing,
        // e.g. after a manual P/Esc pause).
        const music = this.registry.get('music') as any;
        if (music && music.isPaused) {
            music.resume();
        }
        this.scene.resume('level');
        this.scene.stop();
    }
}
